import {
    useEffect,
    Fragment,
    useState,
    useMemo,
    type FC,
    useRef,
    useId
} from "react";
import {
    AccessibilityInfo,
    Platform,
    View
} from "react-native";
import type IProgressProps from "./type";
import type {
    ProgressContextType
} from "./type";
import stylesheet, {
    getProgressColors,
    getProgressSize,
    useStyles
} from "./stylesheet";
import ProgressDescription from "./components/description";
import ProgressHeader from "./components/header";
import ProgressIndicator from "./components/indicator";
import ProgressStep from "./components/step";
import ProgressTitle from "./components/title";
import ProgressTrack from "./components/track";
import ProgressValue from "./components/value";
import {
    ProgressContext
} from "./context";
import {
    PROGRESS_ANNOUNCE_BUCKETS,
    getProgressAnnouncement,
    PROGRESS_DEFAULT_MAX,
    getProgressValueText,
    getProgressValue,
    getProgressMax
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    warnOnce
} from "../../utils/development";
import Text from "../text";

const Progress: FC<IProgressProps> = ({
    isIndeterminate: isIndeterminateProp = false,
    max: maxProp = PROGRESS_DEFAULT_MAX,
    "aria-label": ariaLabel,
    isValueVisible = true,
    value: valueProp = 0,
    accessibilityLabel,
    variant = "linear",
    type = "primary",
    isMeter = false,
    size = "medium",
    customLocalize,
    valueFormatter,
    customTheme,
    description,
    children,
    steps,
    style,
    title,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        announcement,
        setAnnouncement
    ] = useState<string>("");

    const titleID = `${useId().replace(/[^a-zA-Z0-9]/g, "")}-title`;

    const isIndeterminate = isIndeterminateProp && !isMeter;

    const max = getProgressMax({
        max: maxProp,
        variant,
        steps
    });

    const value = getProgressValue({
        value: valueProp,
        variant,
        max
    });

    const percent = value / max;

    const currentSize = useMemo(() => getProgressSize({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        borders,
        spaces,
        size
    }), [
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        borders,
        spaces,
        size
    ]);

    const currentColors = useMemo(() => getProgressColors({
        colors,
        type
    }), [
        colors,
        type
    ]);

    const valueTexts = getProgressValueText({
        locale: activeLocale,
        valueFormatter,
        localize,
        variant,
        value,
        max
    });

    const {
        container: containerDynamicStyle,
        status: statusDynamicStyle
    } = useStyles({
        currentSize,
        borders,
        variant,
        spaces
    });

    const bucket = isIndeterminate || isMeter ? -1 : Math.floor(percent * PROGRESS_ANNOUNCE_BUCKETS);

    const previousBucket = useRef<number>(bucket);

    const previousType = useRef<string>(type);

    const accessibleName = title ?? ariaLabel ?? accessibilityLabel;

    const announce = (message: string) => {
        setAnnouncement(message);

        if(Platform.OS === "ios") {
            AccessibilityInfo.announceForAccessibility(message);
        }
    };

    useEffect(() => {
        const lastBucket = previousBucket.current;

        previousBucket.current = bucket;

        if(bucket <= lastBucket) {
            return;
        }

        announce(getProgressAnnouncement({
            accessibleText: valueTexts.accessibleText,
            title: accessibleName
        }));
    }, [
        bucket
    ]);

    useEffect(() => {
        const lastType = previousType.current;

        previousType.current = type;

        if(type !== "danger" || lastType === "danger") {
            return;
        }

        announce(getProgressAnnouncement({
            accessibleText: description ?? valueTexts.accessibleText,
            title: accessibleName
        }));
    }, [
        type
    ]);

    useEffect(() => {
        if(accessibleName) {
            return;
        }

        warnOnce({
            message: "Progress: a progress indicator needs a \"title\" (or an \"aria-label\" / \"accessibilityLabel\") so screen readers can name it."
        });
    }, [
        accessibleName
    ]);

    const context: ProgressContextType = useMemo(() => ({
        accessibleValueText: isIndeterminate ? undefined : valueTexts.accessibleText,
        valueText: isIndeterminate ? undefined : valueTexts.valueText,
        steps: variant === "segmented" ? max : 0,
        isIndeterminate,
        accessibleName,
        isValueVisible,
        currentColors,
        currentSize,
        customTheme,
        description,
        isMeter,
        percent,
        titleID,
        variant,
        title,
        value,
        size,
        type,
        max
    }), [
        valueTexts.accessibleText,
        valueTexts.valueText,
        isIndeterminate,
        accessibleName,
        isValueVisible,
        currentColors,
        currentSize,
        customTheme,
        description,
        isMeter,
        percent,
        titleID,
        variant,
        title,
        value,
        size,
        type,
        max
    ]);

    const renderDefaultLayout = () => {
        if(variant === "circular") {
            return <ProgressTrack />;
        }

        const isShowHeader = !!title || (isValueVisible && !isIndeterminate);

        return <Fragment>
            {
                isShowHeader ?
                    <ProgressHeader />
                :
                    null
            }
            <ProgressTrack />
            {
                description ?
                    <ProgressDescription />
                :
                    null
            }
        </Fragment>;
    };

    return <ProgressContext.Provider
        value={context}
    >
        <View
            {...props}
            style={[
                style,
                stylesheet.container,
                variant === "circular" ? stylesheet.ring : undefined,
                containerDynamicStyle
            ]}
        >
            {children ?? renderDefaultLayout()}
            <View
                style={[
                    stylesheet.status,
                    statusDynamicStyle
                ]}
                aria-live="polite"
                role="status"
            >
                {
                    announcement ?
                        <Text
                            customTheme={customTheme}
                        >
                            {announcement}
                        </Text>
                    :
                        null
                }
            </View>
        </View>
    </ProgressContext.Provider>;
};
export default Object.assign(Progress, {
    Description: ProgressDescription,
    Indicator: ProgressIndicator,
    Header: ProgressHeader,
    Title: ProgressTitle,
    Track: ProgressTrack,
    Value: ProgressValue,
    Step: ProgressStep
});
